import { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

import { alignItems, strCode } from "../../../../consts";
import { t } from "../../../../services";

import { FlexContainers, Input, SmallButton, WrapperContainer } from "../../../../components";
import { versionUtils } from "../../FWUCampaign/NewFWUCampaignModal/utils/versionUtils";

export const VersionInput = ({
  versions = [],
  maxVersion,
  patchVersion,
  onAddVersion,
  disabled,
  hasError,
  handleSelectPatch = () => {}
}) => {
  const [major, setMajor] = useState("");
  const [minor, setMinor] = useState("");
  const [patch, setPatch] = useState("");

  const inputIds = ["major", "minor", "patch"];

  const maxVersionPatch = useMemo(() => {
    return maxVersion ? maxVersion.split(".")[2] : null;
  }, [maxVersion]);

  const version = useMemo(() => {
    return `${major}.${minor}.${patch}`;
  }, [major, minor, patch]);

  const versionExists = useMemo(() => {
    return !!versions.find((ver) => ver.id === version);
  }, [version, versions]);

  const isValid = useMemo(() => {
    let valid = !versionExists;

    if (maxVersion && valid && major && minor && patch) {
      const maxVersionParts = maxVersion.split(".").map((part) => parseInt(part));
      valid = versionUtils.compareVersions(maxVersionParts, [major, minor, patch]);
    }

    return !!valid;
  }, [versionExists, version, maxVersion]);

  useEffect(() => {
    if (patchVersion !== null) {
      setPatch(patchVersion);
    } else if (maxVersionPatch !== null) {
      setPatch(maxVersionPatch);
    }
  }, [maxVersionPatch, patchVersion]);

  const getNeighbouringIds = (id) => {
    const idIndex = inputIds.indexOf(id);

    const prevIdIndex = idIndex - 1 >= 0 ? idIndex - 1 : inputIds.length - 1;
    const nextIdIndex = idIndex + 1 < inputIds.length ? idIndex + 1 : 0;

    return { prevIdIndex, nextIdIndex };
  };

  const handleSetValue = (id, value) => {
    const { nextIdIndex } = getNeighbouringIds(id);

    if (value?.length === 2) {
      document.getElementById(inputIds[nextIdIndex]).focus();
    }
  };

  const handleKeyDown = (e, id, value) => {
    const key = e.key;
    const { prevIdIndex, nextIdIndex } = getNeighbouringIds(id);

    if (["Enter", "Tab"].includes(key)) {
      document.getElementById(inputIds[nextIdIndex]).focus();
    }

    if (key === "Backspace" && value?.length === 0) {
      e.preventDefault();
      document.getElementById(inputIds[prevIdIndex]).focus();
    }
  };

  const handleAddVersion = () => {
    const version = `${major}.${minor}.${patch}`;
    onAddVersion(version);

    setMajor("");
    setMinor("");

    if (maxVersionPatch === null && patchVersion === null) {
      setPatch("");
    }
  };

  return (
    <WrapperContainer
      Container={FlexContainers.FlexRow}
      alignItems={alignItems.end}
      gap={8}
      width={"100%"}>
      <Input
        id={inputIds[0]}
        title={t(strCode.MAJOR_TITLE)}
        placeholder={"0"}
        value={major}
        setValue={(value) => {
          if (value >= 0) {
            setMajor(value);
            handleSetValue(inputIds[0], value);
          }
        }}
        onKeyDown={(e) => handleKeyDown(e, inputIds[0], major)}
        type="number"
        maxLength={2}
        disabled={disabled}
        hasError={hasError}
      />
      <Input
        id={inputIds[1]}
        title={t(strCode.MINOR_TITLE)}
        placeholder={"0"}
        value={minor}
        setValue={(value) => {
          if (value >= 0) {
            setMinor(value);
            handleSetValue(inputIds[1], value);
          }
        }}
        onKeyDown={(e) => handleKeyDown(e, inputIds[1], minor)}
        type="number"
        maxLength={2}
        disabled={disabled}
        hasError={hasError}
      />
      <Input
        id={inputIds[2]}
        title={t(strCode.PATCH_TITLE)}
        placeholder={"0"}
        value={patch}
        setValue={(value) => {
          if (value >= 0) {
            setPatch(value);
            handleSelectPatch(value);
            handleSetValue(inputIds[2], value);
          }
        }}
        onKeyDown={(e) => handleKeyDown(e, inputIds[2], patch)}
        type="number"
        maxLength={2}
        disabled={maxVersionPatch !== null || patchVersion !== null || disabled}
        hasError={hasError}
      />

      <SmallButton onClick={handleAddVersion} disabled={!major || !minor || !patch || !isValid}>
        {t(strCode.ADD_BTN)}
      </SmallButton>
    </WrapperContainer>
  );
};

VersionInput.propTypes = {
  versions: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
      .isRequired
  ),
  maxVersion: PropTypes.string,
  patchVersion: PropTypes.string,
  onAddVersion: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  handleSelectPatch: PropTypes.func
};
